import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaPlus, FaTrash, FaEdit } from 'react-icons/fa';
import { getAdminSession, isSuperAdmin } from '../adminSession';
import {
  type AdminEmployee,
  type MainTask,
  type TaskItem,
  type TodoPriority,
  type TodoStatus,
  type CreateTaskItemPayload,
  type NoticeItem,
  fetchEmployees,
  fetchMainTasks,
  fetchTaskItems,
  createMainTask,
  updateMainTask,
  deleteMainTask,
  createTaskItem,
  updateTaskItem,
  deleteTaskItem,
  fetchNotices,
  createNotice,
  updateNoticeItem,
  deleteNoticeItem,
  type PersonalMemoItem,
  fetchPersonalMemos,
  createPersonalMemo,
  updatePersonalMemo,
  deletePersonalMemo,
} from './adminTodoApi';
import './AdminTodoManage.scss';

const FaPlusIcon: any = FaPlus;
const FaTrashIcon: any = FaTrash;
const FaEditIcon: any = FaEdit;

const STATUS_OPTIONS: { value: TodoStatus; label: string }[] = [
  { value: 'todo', label: '대기' },
  { value: 'in_progress', label: '진행중' },
  { value: 'done', label: '완료' },
];

const PRIORITY_OPTIONS: { value: TodoPriority; label: string }[] = [
  { value: 'high', label: '높음' },
  { value: 'medium', label: '중간' },
  { value: 'low', label: '낮음' },
];

function todayDateString(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function normalizeDateText(value: string | null): string | null {
  if (!value) return null;
  const match = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function getYearMonth(dateText: string | null): { year: number; month: number } | null {
  const normalized = normalizeDateText(dateText);
  if (!normalized) return null;
  return {
    year: Number(normalized.slice(0, 4)),
    month: Number(normalized.slice(5, 7)),
  };
}

function monthIndex(year: number, month: number): number {
  return year * 12 + month;
}

function itemVisibleInMonth(item: TaskItem, targetYear: number, targetMonth: number): boolean {
  const start = getYearMonth(item.start_date);
  const due = getYearMonth(item.due_date);

  if (!start && !due) return true;

  const target = monthIndex(targetYear, targetMonth);

  if (start && due) {
    return target >= monthIndex(start.year, start.month) && target <= monthIndex(due.year, due.month);
  }
  if (start && !due) {
    return target >= monthIndex(start.year, start.month);
  }
  if (!start && due) {
    return due.year === targetYear && due.month === targetMonth;
  }
  return false;
}

function itemOverlapsYear(item: TaskItem, targetYear: number): boolean {
  for (let m = 1; m <= 12; m += 1) {
    if (itemVisibleInMonth(item, targetYear, m)) return true;
  }
  return false;
}

function formatDateLabel(dateText: string | null): string {
  const normalized = normalizeDateText(dateText);
  if (!normalized) return '';
  const [y, m, d] = normalized.split('-');
  return `${y}.${m}.${d}`;
}

function formatShortDate(dateText: string | null): string {
  const normalized = normalizeDateText(dateText);
  if (!normalized) return '';
  const [, m, d] = normalized.split('-');
  return `${m}.${d}`;
}

function formatCellDateRange(startDate: string | null, dueDate: string | null): string {
  const start = formatShortDate(startDate);
  const due = formatShortDate(dueDate);
  if (start && due) return `${start}~${due}`;
  if (start) return `${start}~`;
  if (due) return `~${due}`;
  return '';
}


const MONTH_LABELS = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
const MONTHS_PER_PAGE = 3;

function quarterStartMonth(month: number): number {
  return Math.floor((month - 1) / MONTHS_PER_PAGE) * MONTHS_PER_PAGE + 1;
}

function visibleMonthRange(startMonth: number): number[] {
  return [startMonth, startMonth + 1, startMonth + 2];
}

function isDueUrgent(dueDate: string | null): boolean {
  const normalized = normalizeDateText(dueDate);
  if (!normalized) return false;
  return normalized <= todayDateString();
}

function isOverdue(item: TaskItem): boolean {
  return isDueUrgent(item.due_date) && item.status !== 'done';
}

function assigneeLabel(emp: AdminEmployee): string {
  const parts = [emp.name];
  if (emp.department) parts.push(emp.department);
  if (emp.position) parts.push(emp.position);
  return parts.join(' · ');
}

const EMPTY_ITEM_FORM = {
  main_task_id: 0,
  assignee_id: '',
  title: '',
  description: '',
  priority: 'medium' as TodoPriority,
  start_date: '',
  due_date: '',
};

export default function AdminTodoManage() {
  const session = getAdminSession();
  const superAdmin = isSuperAdmin(session);
  const requesterId = session?.id ?? 0;

  const [employees, setEmployees] = useState<AdminEmployee[]>([]);
  const [mainTasks, setMainTasks] = useState<MainTask[]>([]);
  const [taskItems, setTaskItems] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [actingId, setActingId] = useState<number | null>(null);

  const [calendarYear, setCalendarYear] = useState<number>(() => new Date().getFullYear());
  const [monthWindowStart, setMonthWindowStart] = useState<number>(() => quarterStartMonth(new Date().getMonth() + 1));
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');

  // 메인업무 추가/수정
  const [mainTaskFormOpen, setMainTaskFormOpen] = useState(false);
  const [mainTaskEditTarget, setMainTaskEditTarget] = useState<MainTask | null>(null);
  const [mainTaskTitle, setMainTaskTitle] = useState('');

  // 작업 추가/수정
  const [itemFormOpen, setItemFormOpen] = useState(false);
  const [itemForm, setItemForm] = useState(EMPTY_ITEM_FORM);
  const [itemEditOpen, setItemEditOpen] = useState(false);
  const [itemEditTarget, setItemEditTarget] = useState<TaskItem | null>(null);
  const [itemEditForm, setItemEditForm] = useState(EMPTY_ITEM_FORM);

  // 마감일 지정 체크
  const [useStartDate, setUseStartDate] = useState(true);
  const [useDueDate, setUseDueDate] = useState(true);
  const [isTodayWork, setIsTodayWork] = useState(false);
  const [editUseStartDate, setEditUseStartDate] = useState(true);
  const [editUseDueDate, setEditUseDueDate] = useState(true);

  // 셀 상세
  const [detailCell, setDetailCell] = useState<{ mainTaskId: number; month: number } | null>(null);
  const [memoDrafts, setMemoDrafts] = useState<Record<number, string>>({});
  const [memoSavingId, setMemoSavingId] = useState<number | null>(null);

  // 공지사항 목록 (전체 공유, 누구나 작성/수정/삭제)
  const [notices, setNotices] = useState<NoticeItem[]>([]);
  const currentNotice = notices[0] ?? null;
  const pastNotices = notices.slice(1);
  const [noticeHistoryOpen, setNoticeHistoryOpen] = useState(false);
  const [noticeFormOpen, setNoticeFormOpen] = useState(false);
  const [noticeEditTarget, setNoticeEditTarget] = useState<NoticeItem | null>(null);
  const [noticeDraft, setNoticeDraft] = useState('');
  const [noticeDateDraft, setNoticeDateDraft] = useState('');
  const [noticeSaving, setNoticeSaving] = useState(false);
  const [noticeActingId, setNoticeActingId] = useState<number | null>(null);

  // 개인별 메모 (공지사항처럼 목록형, 본인 것만 조회/작성/수정/삭제 가능)
  const [personalMemos, setPersonalMemos] = useState<PersonalMemoItem[]>([]);
  const currentMemo = personalMemos[0] ?? null;
  const pastMemos = personalMemos.slice(1);
  const [memoHistoryOpen, setMemoHistoryOpen] = useState(false);
  const [personalMemoFormOpen, setPersonalMemoFormOpen] = useState(false);
  const [personalMemoEditTarget, setPersonalMemoEditTarget] = useState<PersonalMemoItem | null>(null);
  const [personalMemoDraft, setPersonalMemoDraft] = useState('');
  const [personalMemoSaving, setPersonalMemoSaving] = useState(false);
  const [personalMemoActingId, setPersonalMemoActingId] = useState<number | null>(null);

  const loadData = useCallback(async () => {
    if (!requesterId) { setError('로그인 정보가 없습니다.'); setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const [empList, mtList, itemList] = await Promise.all([
        fetchEmployees(requesterId),
        fetchMainTasks(requesterId),
        fetchTaskItems(requesterId),
      ]);
      setEmployees(empList);
      setMainTasks(mtList);
      setTaskItems(itemList);
    } catch {
      setError('데이터를 불러오지 못했습니다.');
      setEmployees([]); setMainTasks([]); setTaskItems([]);
    } finally {
      setLoading(false);
    }

    // 공지사항은 별도 실패해도 나머지 데이터 로딩에 영향을 주지 않음
    try {
      const list = await fetchNotices(requesterId);
      setNotices(list);
    } catch {
      // 공지 API가 아직 배포되지 않았을 수 있음 — 조용히 무시
    }

    // 개인별 메모도 별도 실패해도 나머지 데이터 로딩에 영향을 주지 않음
    try {
      const list = await fetchPersonalMemos(requesterId);
      setPersonalMemos(list);
    } catch {
      // 메모 API가 아직 배포되지 않았을 수 있음 — 조용히 무시
    }
  }, [requesterId]);

  useEffect(() => { void loadData(); }, [loadData]);

  // ─── 연간 표 (가로축: 3개월씩) ───
  const year = calendarYear;
  const visibleMonths = useMemo(() => visibleMonthRange(monthWindowStart), [monthWindowStart]);
  const windowLabel = visibleMonths.map((m) => MONTH_LABELS[m - 1]).join(' · ');
  const todayStr = todayDateString();
  const todayYear = Number(todayStr.slice(0, 4));
  const todayMonth = Number(todayStr.slice(5, 7));

  const goPrevWindow = () => {
    setDetailCell(null);
    if (monthWindowStart === 1) {
      setCalendarYear((y) => y - 1);
      setMonthWindowStart(10);
      return;
    }
    setMonthWindowStart((s) => s - MONTHS_PER_PAGE);
  };

  const goNextWindow = () => {
    setDetailCell(null);
    if (monthWindowStart === 10) {
      setCalendarYear((y) => y + 1);
      setMonthWindowStart(1);
      return;
    }
    setMonthWindowStart((s) => s + MONTHS_PER_PAGE);
  };

  const goToCurrentWindow = () => {
    setDetailCell(null);
    setCalendarYear(todayYear);
    setMonthWindowStart(quarterStartMonth(todayMonth));
  };

  const scopedItems = useMemo(() => {
    let list = taskItems;
    if (assigneeFilter !== 'all') {
      list = list.filter((t) => String(t.assignee_id) === assigneeFilter);
    }
    return list.filter((t) => itemOverlapsYear(t, year));
  }, [taskItems, assigneeFilter, year]);

  const activeScopedItems = useMemo(
    () => scopedItems.filter((t) => t.status !== 'done'),
    [scopedItems],
  );

  const completedItems = useMemo(() => {
    return scopedItems
      .filter((t) => t.status === 'done')
      .slice()
      .sort((a, b) => {
        const aDue = a.due_date || a.start_date || '';
        const bDue = b.due_date || b.start_date || '';
        if (aDue !== bDue) return bDue.localeCompare(aDue);
        return b.id - a.id;
      });
  }, [scopedItems]);

  type CellKey = string;
  const cellMap = useMemo(() => {
    const map = new Map<CellKey, TaskItem[]>();
    for (const item of activeScopedItems) {
      for (let m = 1; m <= 12; m += 1) {
        if (!itemVisibleInMonth(item, year, m)) continue;
        const key = `${item.main_task_id}|${m}`;
        const arr = map.get(key);
        if (arr) arr.push(item); else map.set(key, [item]);
      }
    }
    return map;
  }, [activeScopedItems, year]);

  const windowItems = useMemo(() => {
    return activeScopedItems.filter((t) =>
      visibleMonths.some((m) => itemVisibleInMonth(t, year, m))
    );
  }, [activeScopedItems, visibleMonths, year]);

  const stats = useMemo(() => ({
    total: windowItems.length + completedItems.filter((t) =>
      visibleMonths.some((m) => itemVisibleInMonth(t, year, m))
    ).length,
    done: completedItems.filter((t) =>
      visibleMonths.some((m) => itemVisibleInMonth(t, year, m))
    ).length,
    inProgress: windowItems.filter((t) => t.status === 'in_progress').length,
    overdue: windowItems.filter((t) => isOverdue(t)).length,
  }), [windowItems, completedItems, visibleMonths, year]);

  const detailItems = useMemo(() => {
    if (!detailCell) return [];
    return cellMap.get(`${detailCell.mainTaskId}|${detailCell.month}`) ?? [];
  }, [detailCell, cellMap]);

  useEffect(() => {
    if (detailCell && detailItems.length === 0) {
      setDetailCell(null);
    }
  }, [detailCell, detailItems]);

  // ─── 메인업무 CRUD ───
  const openMainTaskCreate = () => {
    setMainTaskEditTarget(null);
    setMainTaskTitle('');
    setMainTaskFormOpen(true);
  };

  const openMainTaskEdit = (mt: MainTask) => {
    if (mt.created_by !== requesterId) {
      alert('등록한 사람만 수정할 수 있습니다.');
      return;
    }
    setMainTaskEditTarget(mt);
    setMainTaskTitle(mt.title);
    setMainTaskFormOpen(true);
  };

  const handleMainTaskSave = async () => {
    const title = mainTaskTitle.trim();
    if (!title) { alert('메인업무 이름을 입력해 주세요.'); return; }
    setSaving(true);
    try {
      if (mainTaskEditTarget) {
        const updated = await updateMainTask(requesterId, mainTaskEditTarget.id, { title });
        setMainTasks((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
      } else {
        const created = await createMainTask(requesterId, { title });
        setMainTasks((prev) => [...prev, created]);
      }
      setMainTaskFormOpen(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const handleMainTaskDelete = async (mt: MainTask) => {
    if (!superAdmin) {
      alert('대표만 삭제할 수 있습니다.');
      return;
    }
    if (!window.confirm(`"${mt.title}" 메인업무와 하위 작업을 모두 삭제할까요?`)) return;
    try {
      await deleteMainTask(requesterId, mt.id);
      setMainTasks((prev) => prev.filter((m) => m.id !== mt.id));
      setTaskItems((prev) => prev.filter((i) => i.main_task_id !== mt.id));
      if (detailCell?.mainTaskId === mt.id) setDetailCell(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : '삭제에 실패했습니다.');
    }
  };

  // ─── 작업 CRUD ───
  const openItemCreate = (mainTaskId: number, monthNum?: number) => {
    const monthDate = monthNum
      ? `${year}-${String(monthNum).padStart(2, '0')}-01`
      : '';
    setIsTodayWork(false);
    setUseStartDate(!!monthNum);
    setUseDueDate(!!monthNum);
    setItemForm({
      ...EMPTY_ITEM_FORM,
      main_task_id: mainTaskId,
      assignee_id: superAdmin ? '' : String(requesterId),
      start_date: monthDate,
      due_date: monthDate,
    });
    setItemFormOpen(true);
  };

  const handleItemCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const title = itemForm.title.trim();
    if (!title) { alert('제목을 입력해 주세요.'); return; }
    const assigneeId = superAdmin ? Number(itemForm.assignee_id) : requesterId;
    if (!assigneeId) { alert('담당자를 선택해 주세요.'); return; }

    const startDate = useStartDate ? (itemForm.start_date.trim() || null) : null;
    const dueDate = useDueDate ? (itemForm.due_date.trim() || null) : null;
    if (startDate && dueDate && startDate > dueDate) {
      alert('시작일은 마감일보다 이후일 수 없습니다.');
      return;
    }

    const payload: CreateTaskItemPayload = {
      main_task_id: itemForm.main_task_id,
      assignee_id: assigneeId,
      title,
      priority: itemForm.priority,
      description: itemForm.description.trim() || undefined,
      status: isTodayWork ? 'done' : 'todo',
      start_date: startDate,
      due_date: dueDate,
    };
    setSaving(true);
    try {
      const created = await createTaskItem(requesterId, payload);
      const saved = isTodayWork ? { ...created, status: 'done' as const } : created;
      setTaskItems((prev) => [...prev, saved]);
      setItemFormOpen(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : '등록에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const openItemEdit = (item: TaskItem) => {
    if ((item.created_by ?? item.assignee_id) !== requesterId) {
      alert('등록한 사람만 수정할 수 있습니다.');
      return;
    }
    setItemEditTarget(item);
    setEditUseStartDate(!!item.start_date);
    setEditUseDueDate(!!item.due_date);
    setItemEditForm({
      main_task_id: item.main_task_id,
      assignee_id: String(item.assignee_id),
      title: item.title,
      description: item.description || '',
      priority: item.priority,
      start_date: item.start_date || '',
      due_date: item.due_date || '',
    });
    setItemEditOpen(true);
  };

  const handleItemUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!itemEditTarget) return;
    const title = itemEditForm.title.trim();
    if (!title) { alert('제목을 입력해 주세요.'); return; }

    const startDate = editUseStartDate ? (itemEditForm.start_date.trim() || null) : null;
    const dueDate = editUseDueDate ? (itemEditForm.due_date.trim() || null) : null;
    if (startDate && dueDate && startDate > dueDate) {
      alert('시작일은 마감일보다 이후일 수 없습니다.');
      return;
    }

    setSaving(true);
    setActingId(itemEditTarget.id);
    try {
      const updated = await updateTaskItem(requesterId, itemEditTarget.id, {
        title,
        description: itemEditForm.description.trim() || null,
        priority: itemEditForm.priority,
        start_date: startDate,
        due_date: dueDate,
        main_task_id: itemEditForm.main_task_id,
        ...(superAdmin ? { assignee_id: Number(itemEditForm.assignee_id) } : {}),
      });
      setTaskItems((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setItemEditOpen(false);
      setItemEditTarget(null);
    } catch (err) {
      alert(err instanceof Error ? err.message : '수정에 실패했습니다.');
    } finally {
      setSaving(false);
      setActingId(null);
    }
  };

  const handleStatusChange = async (item: TaskItem, status: TodoStatus) => {
    if (item.status === status) return;
    if ((item.created_by ?? item.assignee_id) !== requesterId) {
      alert('등록한 사람만 수정할 수 있습니다.');
      return;
    }
    setActingId(item.id);
    try {
      const updated = await updateTaskItem(requesterId, item.id, { status });
      setTaskItems((prev) =>
        prev.map((t) => (t.id === updated.id ? { ...updated, status } : t)),
      );
    } catch (err) {
      alert(err instanceof Error ? err.message : '상태 변경에 실패했습니다.');
    } finally {
      setActingId(null);
    }
  };

  const openNoticeCreate = () => {
    setNoticeEditTarget(null);
    setNoticeDraft('');
    setNoticeDateDraft(todayDateString());
    setNoticeFormOpen(true);
  };

  const openNoticeEdit = (notice: NoticeItem) => {
    setNoticeEditTarget(notice);
    setNoticeDraft(notice.content);
    setNoticeDateDraft(notice.notice_date || '');
    setNoticeFormOpen(true);
  };

  const handleNoticeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = noticeDraft.trim();
    if (!content) { alert('공지 내용을 입력해 주세요.'); return; }
    setNoticeSaving(true);
    try {
      const payload = { content, notice_date: noticeDateDraft || null };
      if (noticeEditTarget) {
        const updated = await updateNoticeItem(requesterId, noticeEditTarget.id, payload);
        setNotices((prev) => prev.map((n) => (n.id === updated.id ? updated : n)));
      } else {
        const created = await createNotice(requesterId, payload);
        setNotices((prev) => [created, ...prev]);
      }
      setNoticeFormOpen(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : '공지 저장에 실패했습니다.');
    } finally {
      setNoticeSaving(false);
    }
  };

  const handleNoticeDelete = async (notice: NoticeItem) => {
    if (!window.confirm('공지사항을 삭제할까요?')) return;
    setNoticeActingId(notice.id);
    try {
      await deleteNoticeItem(requesterId, notice.id);
      setNotices((prev) => prev.filter((n) => n.id !== notice.id));
    } catch (err) {
      alert(err instanceof Error ? err.message : '공지 삭제에 실패했습니다.');
    } finally {
      setNoticeActingId(null);
    }
  };

  const handleMemoSave = async (item: TaskItem) => {
    if ((item.created_by ?? item.assignee_id) !== requesterId) {
      alert('등록한 사람만 수정할 수 있습니다.');
      return;
    }
    const draft = memoDrafts[item.id] ?? item.description ?? '';
    setMemoSavingId(item.id);
    try {
      const updated = await updateTaskItem(requesterId, item.id, { description: draft.trim() || null });
      setTaskItems((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setMemoDrafts((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : '메모 저장에 실패했습니다.');
    } finally {
      setMemoSavingId(null);
    }
  };

  const openPersonalMemoCreate = () => {
    setPersonalMemoEditTarget(null);
    setPersonalMemoDraft('');
    setPersonalMemoFormOpen(true);
  };

  const openPersonalMemoEdit = (memo: PersonalMemoItem) => {
    setPersonalMemoEditTarget(memo);
    setPersonalMemoDraft(memo.content);
    setPersonalMemoFormOpen(true);
  };

  const handlePersonalMemoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const content = personalMemoDraft.trim();
    if (!content) { alert('메모 내용을 입력해 주세요.'); return; }
    setPersonalMemoSaving(true);
    try {
      if (personalMemoEditTarget) {
        const updated = await updatePersonalMemo(requesterId, personalMemoEditTarget.id, content);
        setPersonalMemos((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
      } else {
        const created = await createPersonalMemo(requesterId, content);
        setPersonalMemos((prev) => [created, ...prev]);
      }
      setPersonalMemoFormOpen(false);
    } catch (err) {
      alert(err instanceof Error ? err.message : '메모 저장에 실패했습니다.');
    } finally {
      setPersonalMemoSaving(false);
    }
  };

  const handlePersonalMemoDelete = async (memo: PersonalMemoItem) => {
    if (!window.confirm('메모를 삭제할까요?')) return;
    setPersonalMemoActingId(memo.id);
    try {
      await deletePersonalMemo(requesterId, memo.id);
      setPersonalMemos((prev) => prev.filter((m) => m.id !== memo.id));
    } catch (err) {
      alert(err instanceof Error ? err.message : '메모 삭제에 실패했습니다.');
    } finally {
      setPersonalMemoActingId(null);
    }
  };

  const handleItemDelete = async (item: TaskItem) => {
    if (!superAdmin) {
      alert('대표만 삭제할 수 있습니다.');
      return;
    }
    if (!window.confirm(`"${item.title}" 작업을 삭제할까요?`)) return;
    setActingId(item.id);
    try {
      await deleteTaskItem(requesterId, item.id);
      setTaskItems((prev) => prev.filter((t) => t.id !== item.id));
    } catch (err) {
      alert(err instanceof Error ? err.message : '삭제에 실패했습니다.');
    } finally {
      setActingId(null);
    }
  };

  if (!session?.id) {
    return <div className="admin-todo-manage"><p className="admin-todo-manage__empty">로그인 후 이용할 수 있습니다.</p></div>;
  }

  return (
    <div className="admin-todo-manage">
      {/* 헤더 */}
      <header className="admin-todo-manage__header">
        <div>
          <h1 className="admin-todo-manage__title">업무 관리</h1>
          <p className="admin-todo-manage__subtitle">
            {year}년 {windowLabel} · 총 {stats.total}개 중 {stats.done}개 완료 · 전체 공유(대표·직원 모두 조회) · 수정은 등록자, 삭제는 대표만
          </p>
        </div>
        <div className="admin-todo-manage__header-actions">
          <button type="button" className="admin-todo-manage__refresh-btn" onClick={() => void loadData()} disabled={loading}>새로고침</button>
          <button type="button" className="admin-todo-manage__add-btn" onClick={openMainTaskCreate}><FaPlusIcon /> 메인업무 추가</button>
        </div>
      </header>

      {/* 툴바 */}
      <div className="admin-todo-manage__toolbar">
        <div className="admin-todo-manage__month-nav">
          <button type="button" className="admin-todo-manage__month-btn" onClick={goPrevWindow}>‹ 이전</button>
          <strong className="admin-todo-manage__month-label">{year}년 {windowLabel}</strong>
          <button type="button" className="admin-todo-manage__month-btn" onClick={goNextWindow}>다음 ›</button>
          <button type="button" className="admin-todo-manage__month-btn admin-todo-manage__month-btn--today" onClick={goToCurrentWindow}>이번 분기</button>
        </div>
        {employees.length > 0 && (
          <div className="admin-todo-manage__toolbar-right">
            <select className="admin-todo-manage__assignee-select" value={assigneeFilter} onChange={(e) => setAssigneeFilter(e.target.value)}>
              <option value="all">전체 (공유)</option>
              {employees.map((emp) => <option key={emp.id} value={String(emp.id)}>{assigneeLabel(emp)}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* 공지사항 목록 (전체 공유, 누구나 작성/수정/삭제 가능) */}
      <div className="admin-todo-manage__notice-board">
        <div className="admin-todo-manage__notice-board-header">
          <strong className="admin-todo-manage__notice-board-title">📌 공지사항</strong>
          <button type="button" className="admin-todo-manage__add-btn admin-todo-manage__add-btn--sm" onClick={openNoticeCreate}>
            <FaPlusIcon /> 새로 쓰기
          </button>
        </div>
        {!currentNotice ? (
          <p className="admin-todo-manage__notice-board-empty">등록된 공지사항이 없습니다.</p>
        ) : (
          <div className="admin-todo-manage__notice-item">
            <div className="admin-todo-manage__notice-item-header">
              <span className="admin-todo-manage__notice-item-date">
                {currentNotice.notice_date ? formatDateLabel(currentNotice.notice_date) : '날짜 미지정'}
              </span>
              <span className="admin-todo-manage__notice-item-meta">
                {currentNotice.updated_by_name ? `${currentNotice.updated_by_name} 수정` : ''}
              </span>
              <div className="admin-todo-manage__notice-item-actions">
                <button type="button" className="admin-todo-manage__icon-btn" title="수정" onClick={() => openNoticeEdit(currentNotice)}><FaEditIcon /></button>
                <button
                  type="button"
                  className="admin-todo-manage__icon-btn admin-todo-manage__icon-btn--danger"
                  title="삭제"
                  disabled={noticeActingId === currentNotice.id}
                  onClick={() => void handleNoticeDelete(currentNotice)}
                >
                  <FaTrashIcon />
                </button>
              </div>
            </div>
            <p className="admin-todo-manage__notice-item-content">{currentNotice.content}</p>
          </div>
        )}
        {pastNotices.length > 0 && (
          <div className="admin-todo-manage__notice-history">
            <button
              type="button"
              className="admin-todo-manage__notice-history-toggle"
              onClick={() => setNoticeHistoryOpen((v) => !v)}
            >
              {noticeHistoryOpen ? '지난 공지 접기 ▲' : `지난 공지 ${pastNotices.length}건 보기 ▼`}
            </button>
            {noticeHistoryOpen && (
              <ul className="admin-todo-manage__notice-list">
                {pastNotices.map((notice) => (
                  <li key={notice.id} className="admin-todo-manage__notice-item admin-todo-manage__notice-item--past">
                    <div className="admin-todo-manage__notice-item-header">
                      <span className="admin-todo-manage__notice-item-date">
                        {notice.notice_date ? formatDateLabel(notice.notice_date) : '날짜 미지정'}
                      </span>
                      <span className="admin-todo-manage__notice-item-meta">
                        {notice.updated_by_name ? `${notice.updated_by_name} 수정` : ''}
                      </span>
                      <div className="admin-todo-manage__notice-item-actions">
                        <button type="button" className="admin-todo-manage__icon-btn" title="수정" onClick={() => openNoticeEdit(notice)}><FaEditIcon /></button>
                        <button
                          type="button"
                          className="admin-todo-manage__icon-btn admin-todo-manage__icon-btn--danger"
                          title="삭제"
                          disabled={noticeActingId === notice.id}
                          onClick={() => void handleNoticeDelete(notice)}
                        >
                          <FaTrashIcon />
                        </button>
                      </div>
                    </div>
                    <p className="admin-todo-manage__notice-item-content">{notice.content}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* 개인별 메모 (공지사항처럼 목록형, 로그인한 본인 것만 조회/작성/수정/삭제 가능) */}
      <div className="admin-todo-manage__notice-board">
        <div className="admin-todo-manage__notice-board-header">
          <strong className="admin-todo-manage__notice-board-title">📝 개인별 메모</strong>
          <button type="button" className="admin-todo-manage__add-btn admin-todo-manage__add-btn--sm" onClick={openPersonalMemoCreate}>
            <FaPlusIcon /> 새로 쓰기
          </button>
        </div>
        {!currentMemo ? (
          <p className="admin-todo-manage__notice-board-empty">작성된 메모가 없습니다. 나만 볼 수 있는 메모입니다.</p>
        ) : (
          <div className="admin-todo-manage__notice-item">
            <div className="admin-todo-manage__notice-item-header">
              <span className="admin-todo-manage__notice-item-date">{formatDateLabel(currentMemo.created_at)}</span>
              <div className="admin-todo-manage__notice-item-actions">
                <button type="button" className="admin-todo-manage__icon-btn" title="수정" onClick={() => openPersonalMemoEdit(currentMemo)}><FaEditIcon /></button>
                <button
                  type="button"
                  className="admin-todo-manage__icon-btn admin-todo-manage__icon-btn--danger"
                  title="삭제"
                  disabled={personalMemoActingId === currentMemo.id}
                  onClick={() => void handlePersonalMemoDelete(currentMemo)}
                >
                  <FaTrashIcon />
                </button>
              </div>
            </div>
            <p className="admin-todo-manage__notice-item-content">{currentMemo.content}</p>
          </div>
        )}
        {pastMemos.length > 0 && (
          <div className="admin-todo-manage__notice-history">
            <button
              type="button"
              className="admin-todo-manage__notice-history-toggle"
              onClick={() => setMemoHistoryOpen((v) => !v)}
            >
              {memoHistoryOpen ? '지난 메모 접기 ▲' : `지난 메모 ${pastMemos.length}건 보기 ▼`}
            </button>
            {memoHistoryOpen && (
              <ul className="admin-todo-manage__notice-list">
                {pastMemos.map((memo) => (
                  <li key={memo.id} className="admin-todo-manage__notice-item admin-todo-manage__notice-item--past">
                    <div className="admin-todo-manage__notice-item-header">
                      <span className="admin-todo-manage__notice-item-date">{formatDateLabel(memo.created_at)}</span>
                      <div className="admin-todo-manage__notice-item-actions">
                        <button type="button" className="admin-todo-manage__icon-btn" title="수정" onClick={() => openPersonalMemoEdit(memo)}><FaEditIcon /></button>
                        <button
                          type="button"
                          className="admin-todo-manage__icon-btn admin-todo-manage__icon-btn--danger"
                          title="삭제"
                          disabled={personalMemoActingId === memo.id}
                          onClick={() => void handlePersonalMemoDelete(memo)}
                        >
                          <FaTrashIcon />
                        </button>
                      </div>
                    </div>
                    <p className="admin-todo-manage__notice-item-content">{memo.content}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {error && <div className="admin-todo-manage__error">{error}</div>}

      {loading ? (
        <p className="admin-todo-manage__loading">불러오는 중…</p>
      ) : mainTasks.length === 0 ? (
        <div className="admin-todo-manage__empty-state">
          <p>등록된 메인업무가 없습니다.</p>
          <button type="button" className="admin-todo-manage__add-btn" onClick={openMainTaskCreate}><FaPlusIcon /> 메인업무 추가</button>
        </div>
      ) : (
        <>
          {/* 통계 */}
          <div className="admin-todo-manage__stats-bar">
            <div className="admin-todo-manage__stat-item"><span className="admin-todo-manage__stat-number">{stats.total}</span><span>전체</span></div>
            <div className="admin-todo-manage__stat-item"><span className="admin-todo-manage__stat-number">{stats.done}</span><span>완료</span></div>
            <div className="admin-todo-manage__stat-item"><span className="admin-todo-manage__stat-number">{stats.inProgress}</span><span>진행중</span></div>
            <div className="admin-todo-manage__stat-item"><span className="admin-todo-manage__stat-number">{stats.overdue}</span><span>지연</span></div>
          </div>

          {/* 표 */}
          <div className="admin-todo-manage__table-scroll">
            <table className="admin-todo-manage__table">
              <thead>
                <tr>
                  <th className="admin-todo-manage__th admin-todo-manage__th--service">메인업무</th>
                  {visibleMonths.map((m) => {
                    const isCurrentMonth = year === todayYear && m === todayMonth;
                    return (
                      <th key={m} className={`admin-todo-manage__th admin-todo-manage__th--month${isCurrentMonth ? ' admin-todo-manage__th--today' : ''}`}>
                        <span className="admin-todo-manage__th-month">{MONTH_LABELS[m - 1]}</span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {mainTasks.map((mt) => (
                  <tr key={mt.id}>
                    <td className="admin-todo-manage__td admin-todo-manage__td--service">
                      <div className="admin-todo-manage__service-cell">
                        <span className="admin-todo-manage__service-name">{mt.title}</span>
                        <div className="admin-todo-manage__service-actions">
                          {mt.created_by === requesterId ? (
                            <button type="button" className="admin-todo-manage__icon-btn" title="수정" onClick={() => openMainTaskEdit(mt)}><FaEditIcon /></button>
                          ) : null}
                          {superAdmin ? (
                            <button type="button" className="admin-todo-manage__icon-btn admin-todo-manage__icon-btn--danger" title="삭제" onClick={() => void handleMainTaskDelete(mt)}><FaTrashIcon /></button>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    {visibleMonths.map((m) => {
                      const key = `${mt.id}|${m}`;
                      const cellItems = cellMap.get(key) ?? [];
                      const isCurrentMonth = year === todayYear && m === todayMonth;
                      const total = cellItems.length;
                      const hasOverdue = cellItems.some((t) => isOverdue(t));
                      const isActive = detailCell?.mainTaskId === mt.id && detailCell?.month === m;

                      return (
                        <td
                          key={m}
                          className={`admin-todo-manage__td admin-todo-manage__td--cell${isCurrentMonth ? ' admin-todo-manage__td--today' : ''}${hasOverdue ? ' admin-todo-manage__td--overdue' : ''}${isActive ? ' admin-todo-manage__td--active' : ''}`}
                          onClick={() => {
                            if (isActive) setDetailCell(null);
                            else if (total > 0) setDetailCell({ mainTaskId: mt.id, month: m });
                            else openItemCreate(mt.id, m);
                          }}
                          title={total > 0 ? cellItems.map((t) => t.title).join(', ') : `${mt.title} · ${MONTH_LABELS[m - 1]} — 클릭하여 추가`}
                        >
                          {total > 0 && (
                            <div className="admin-todo-manage__cell-content">
                              {cellItems.map((item) => {
                                const dateRange = formatCellDateRange(item.start_date, item.due_date);
                                return (
                                  <span
                                    key={item.id}
                                    className={`admin-todo-manage__cell-title admin-todo-manage__cell-title--${item.status}${isOverdue(item) ? ' admin-todo-manage__cell-title--overdue' : ''}`}
                                    title={dateRange ? `${item.title} (${dateRange})` : item.title}
                                  >
                                    <span className="admin-todo-manage__cell-title-text">{item.title}</span>
                                    {dateRange && (
                                      <span className="admin-todo-manage__cell-title-dates">{dateRange}</span>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 셀 상세 패널 */}
          {detailCell && detailItems.length > 0 && (
            <div className="admin-todo-manage__detail-panel">
              <div className="admin-todo-manage__detail-header">
                <strong>{mainTasks.find((m) => m.id === detailCell.mainTaskId)?.title} · {MONTH_LABELS[detailCell.month - 1]}</strong>
                <div className="admin-todo-manage__detail-actions">
                  <button type="button" className="admin-todo-manage__add-btn admin-todo-manage__add-btn--sm" onClick={() => openItemCreate(detailCell.mainTaskId, detailCell.month)}><FaPlusIcon /> 추가</button>
                  <button type="button" className="admin-todo-manage__detail-close" onClick={() => setDetailCell(null)}>×</button>
                </div>
              </div>
              <ul className="admin-todo-manage__detail-list">
                {detailItems.map((item) => {
                  const canEdit = (item.created_by ?? item.assignee_id) === requesterId;
                  const canDelete = superAdmin;
                  return (
                  <li key={item.id} className={`admin-todo-manage__detail-item${isOverdue(item) ? ' admin-todo-manage__detail-item--urgent' : ''}`}>
                    <div className="admin-todo-manage__detail-item-row">
                      <span className={`admin-todo-manage__status-badge admin-todo-manage__status-badge--${item.status}`}>
                        {STATUS_OPTIONS.find((s) => s.value === item.status)?.label ?? item.status}
                      </span>
                      <div className="admin-todo-manage__detail-body">
                        <span className="admin-todo-manage__detail-title">{item.title}</span>
                        <span className="admin-todo-manage__detail-meta">
                          {item.assignee_name}
                          {' · '}
                          <span className={`admin-todo-manage__priority-inline admin-todo-manage__priority-inline--${item.priority}`}>
                            {PRIORITY_OPTIONS.find((p) => p.value === item.priority)?.label}
                          </span>
                          {(item.start_date || item.due_date) && (
                            <>
                              {' · '}
                              {item.start_date ? formatDateLabel(item.start_date) : '시작 미정'}
                              {' ~ '}
                              {item.due_date ? formatDateLabel(item.due_date) : '마감 미정'}
                            </>
                          )}
                        </span>
                      </div>
                      {(canEdit || canDelete) ? (
                      <div className="admin-todo-manage__detail-btns">
                        {canEdit
                          ? STATUS_OPTIONS.map((s) => (
                          <button key={s.value} type="button" className={`admin-todo-manage__status-btn admin-todo-manage__status-btn--${s.value}${item.status === s.value ? ' admin-todo-manage__status-btn--active' : ''}`} disabled={actingId === item.id} onClick={() => void handleStatusChange(item, s.value)}>{s.label}</button>
                        ))
                          : null}
                        {canEdit ? (
                        <button type="button" className="admin-todo-manage__edit-btn" disabled={actingId === item.id} onClick={() => openItemEdit(item)}>수정</button>
                        ) : null}
                        {canDelete ? (
                        <button type="button" className="admin-todo-manage__delete-btn" disabled={actingId === item.id} onClick={() => void handleItemDelete(item)}>삭제</button>
                        ) : null}
                      </div>
                      ) : null}
                    </div>
                    <div className="admin-todo-manage__detail-memo">
                      <textarea
                        className="admin-todo-manage__detail-memo-input"
                        readOnly={!canEdit}
                        rows={2}
                        value={memoDrafts[item.id] ?? item.description ?? ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setMemoDrafts((prev) => ({ ...prev, [item.id]: value }));
                        }}
                        placeholder="메모 없음"
                      />
                      {canEdit ? (
                        <button
                          type="button"
                          className="admin-todo-manage__memo-save-btn"
                          disabled={memoSavingId === item.id || (memoDrafts[item.id] ?? item.description ?? '') === (item.description ?? '')}
                          onClick={() => void handleMemoSave(item)}
                        >
                          {memoSavingId === item.id ? '저장 중…' : '메모저장'}
                        </button>
                      ) : null}
                    </div>
                  </li>
                  );
                })}
              </ul>
            </div>
          )}

          <section className="admin-todo-manage__completed">
            <div className="admin-todo-manage__completed-header">
              <h2 className="admin-todo-manage__completed-title">완료된 작업</h2>
              <span className="admin-todo-manage__completed-count">{completedItems.length}건</span>
            </div>
            {completedItems.length === 0 ? (
              <p className="admin-todo-manage__completed-empty">
                완료 버튼을 누르면 여기에 표시됩니다.
              </p>
            ) : (
              <div className="admin-todo-manage__completed-table">
                <div className="admin-todo-manage__completed-row admin-todo-manage__completed-row--head">
                  <span>메인업무</span>
                  <span>제목</span>
                  <span>내용</span>
                  <span>담당자</span>
                  <span>우선순위</span>
                  <span>기간</span>
                  <span>관리</span>
                </div>
                {completedItems.map((item) => {
                  const canEdit = (item.created_by ?? item.assignee_id) === requesterId;
                  const canDelete = superAdmin;
                  const mtTitle = mainTasks.find((m) => m.id === item.main_task_id)?.title ?? '메인업무';
                  const priorityLabel = PRIORITY_OPTIONS.find((p) => p.value === item.priority)?.label ?? '-';
                  const dateRange =
                    item.start_date || item.due_date
                      ? `${item.start_date ? formatDateLabel(item.start_date) : '시작 미정'} ~ ${item.due_date ? formatDateLabel(item.due_date) : '마감 미정'}`
                      : '-';
                  const description = item.description?.trim() || '-';

                  return (
                    <div key={item.id} className="admin-todo-manage__completed-row">
                      <span title={mtTitle}>{mtTitle}</span>
                      <span title={item.title}>{item.title}</span>
                      <span title={description}>{description}</span>
                      <span title={item.assignee_name}>{item.assignee_name}</span>
                      <span>{priorityLabel}</span>
                      <span title={dateRange}>{dateRange}</span>
                      <span className="admin-todo-manage__completed-actions">
                        {canEdit ? (
                          <button
                            type="button"
                            className="admin-todo-manage__edit-btn"
                            disabled={actingId === item.id}
                            onClick={() => void handleStatusChange(item, 'todo')}
                          >
                            되돌리기
                          </button>
                        ) : null}
                        {canDelete ? (
                          <button
                            type="button"
                            className="admin-todo-manage__delete-btn"
                            disabled={actingId === item.id}
                            onClick={() => void handleItemDelete(item)}
                          >
                            삭제
                          </button>
                        ) : null}
                        {!canEdit && !canDelete ? (
                          <span className="admin-todo-manage__completed-dash">-</span>
                        ) : null}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}

      {/* 메인업무 추가/수정 모달 */}
      {mainTaskFormOpen && (
        <div className="admin-todo-manage__modal-backdrop" role="presentation" onClick={(e) => { if (e.target === e.currentTarget) setMainTaskFormOpen(false); }}>
          <div className="admin-todo-manage__modal" role="dialog">
            <div className="admin-todo-manage__modal-header">
              <h2 className="admin-todo-manage__modal-title">{mainTaskEditTarget ? '메인업무 수정' : '메인업무 추가'}</h2>
              <button type="button" className="admin-todo-manage__modal-close" onClick={() => setMainTaskFormOpen(false)} aria-label="닫기">×</button>
            </div>
            <div className="admin-todo-manage__form">
              <div className="admin-todo-manage__field">
                <label htmlFor="mt-title">메인업무 이름 *</label>
                <input id="mt-title" type="text" value={mainTaskTitle} onChange={(e) => setMainTaskTitle(e.target.value)} placeholder="예: 프론트엔드 개발, 마케팅, 고객지원…" autoFocus />
              </div>
              <div className="admin-todo-manage__form-actions">
                <button type="button" className="admin-todo-manage__btn-secondary" onClick={() => setMainTaskFormOpen(false)}>취소</button>
                <button type="button" className="admin-todo-manage__btn-primary" disabled={saving} onClick={() => void handleMainTaskSave()}>{saving ? '저장 중…' : '저장'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 작업 추가 모달 */}
      {itemFormOpen && (
        <div className="admin-todo-manage__modal-backdrop" role="presentation" onClick={(e) => { if (e.target === e.currentTarget) setItemFormOpen(false); }}>
          <div className="admin-todo-manage__modal" role="dialog">
            <div className="admin-todo-manage__modal-header">
              <h2 className="admin-todo-manage__modal-title">작업 추가</h2>
              <button type="button" className="admin-todo-manage__modal-close" onClick={() => setItemFormOpen(false)} aria-label="닫기">×</button>
            </div>
            <form onSubmit={handleItemCreate} className="admin-todo-manage__form">
              <div className="admin-todo-manage__field">
                <label htmlFor="item-mt">메인업무</label>
                <select id="item-mt" value={itemForm.main_task_id} onChange={(e) => setItemForm((f) => ({ ...f, main_task_id: Number(e.target.value) }))}>
                  {mainTasks.map((mt) => <option key={mt.id} value={mt.id}>{mt.title}</option>)}
                </select>
              </div>
              {superAdmin ? (
                <div className="admin-todo-manage__field">
                  <label htmlFor="item-assignee">담당 직원 *</label>
                  <select id="item-assignee" required value={itemForm.assignee_id} onChange={(e) => setItemForm((f) => ({ ...f, assignee_id: e.target.value }))}>
                    <option value="">선택</option>
                    {employees.map((emp) => <option key={emp.id} value={String(emp.id)}>{assigneeLabel(emp)}</option>)}
                  </select>
                </div>
              ) : (
                <p className="admin-todo-manage__form-self-hint">담당자: <strong>{session.name}</strong></p>
              )}
              <div className="admin-todo-manage__field">
                <label htmlFor="item-title">제목 *</label>
                <input id="item-title" type="text" required value={itemForm.title} onChange={(e) => setItemForm((f) => ({ ...f, title: e.target.value }))} placeholder="작업 제목" />
              </div>
              <div className="admin-todo-manage__field">
                <label htmlFor="item-desc">내용</label>
                <textarea id="item-desc" rows={3} value={itemForm.description} onChange={(e) => setItemForm((f) => ({ ...f, description: e.target.value }))} placeholder="상세 내용 (선택)" />
              </div>
              <div className="admin-todo-manage__field">
                <label htmlFor="item-priority">우선순위</label>
                <select id="item-priority" value={itemForm.priority} onChange={(e) => setItemForm((f) => ({ ...f, priority: e.target.value as TodoPriority }))}>
                  {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div className="admin-todo-manage__field">
                <label className="admin-todo-manage__checkbox-label">
                  <input
                    type="checkbox"
                    checked={isTodayWork}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setIsTodayWork(checked);
                      if (checked) {
                        setUseStartDate(true);
                        setUseDueDate(true);
                        setItemForm((f) => ({
                          ...f,
                          start_date: todayStr,
                          due_date: todayStr,
                        }));
                      }
                    }}
                  />
                  오늘 한 일
                </label>
                {isTodayWork ? (
                  <p className="admin-todo-manage__field-hint">
                    오늘({todayStr}) 날짜로 설정되며, 완료된 작업에 바로 등록됩니다.
                  </p>
                ) : null}
              </div>
              <div className="admin-todo-manage__field">
                <label className="admin-todo-manage__checkbox-label">
                  <input type="checkbox" checked={useStartDate} onChange={(e) => setUseStartDate(e.target.checked)} />
                  시작일 지정
                </label>
                {useStartDate && (
                  <input
                    id="item-start"
                    type="date"
                    value={itemForm.start_date}
                    onChange={(e) => {
                      const next = e.target.value;
                      setItemForm((f) => ({
                        ...f,
                        start_date: next,
                        ...(useDueDate ? { due_date: next } : {}),
                      }));
                    }}
                    style={{ marginTop: 8 }}
                  />
                )}
                {!useStartDate && (
                  <p className="admin-todo-manage__field-hint">시작일 없이 등록하면 처음부터 표시됩니다.</p>
                )}
              </div>
              <div className="admin-todo-manage__field">
                <label className="admin-todo-manage__checkbox-label">
                  <input type="checkbox" checked={useDueDate} onChange={(e) => setUseDueDate(e.target.checked)} />
                  마감일 지정
                </label>
                {useDueDate && (
                  <input id="item-due" type="date" value={itemForm.due_date} onChange={(e) => setItemForm((f) => ({ ...f, due_date: e.target.value }))} style={{ marginTop: 8 }} />
                )}
                {!useDueDate && (
                  <p className="admin-todo-manage__field-hint">마감일 없이 등록하면 계속 표시됩니다.</p>
                )}
              </div>
              <div className="admin-todo-manage__form-actions">
                <button type="button" className="admin-todo-manage__btn-secondary" onClick={() => setItemFormOpen(false)}>취소</button>
                <button type="submit" className="admin-todo-manage__btn-primary" disabled={saving}>{saving ? '등록 중…' : '등록'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 작업 수정 모달 */}
      {itemEditOpen && itemEditTarget && (
        <div className="admin-todo-manage__modal-backdrop" role="presentation" onClick={(e) => { if (e.target === e.currentTarget) { setItemEditOpen(false); setItemEditTarget(null); } }}>
          <div className="admin-todo-manage__modal" role="dialog">
            <div className="admin-todo-manage__modal-header">
              <h2 className="admin-todo-manage__modal-title">작업 수정</h2>
              <button type="button" className="admin-todo-manage__modal-close" onClick={() => { setItemEditOpen(false); setItemEditTarget(null); }} aria-label="닫기">×</button>
            </div>
            <form onSubmit={handleItemUpdate} className="admin-todo-manage__form">
              <div className="admin-todo-manage__field">
                <label htmlFor="edit-item-mt">메인업무</label>
                <select id="edit-item-mt" value={itemEditForm.main_task_id} onChange={(e) => setItemEditForm((f) => ({ ...f, main_task_id: Number(e.target.value) }))}>
                  {mainTasks.map((mt) => <option key={mt.id} value={mt.id}>{mt.title}</option>)}
                </select>
              </div>
              {superAdmin ? (
                <div className="admin-todo-manage__field">
                  <label htmlFor="edit-item-assignee">담당 직원 *</label>
                  <select id="edit-item-assignee" required value={itemEditForm.assignee_id} onChange={(e) => setItemEditForm((f) => ({ ...f, assignee_id: e.target.value }))}>
                    <option value="">선택</option>
                    {employees.map((emp) => <option key={emp.id} value={String(emp.id)}>{assigneeLabel(emp)}</option>)}
                  </select>
                </div>
              ) : (
                <p className="admin-todo-manage__form-self-hint">담당자: <strong>{itemEditTarget.assignee_name}</strong></p>
              )}
              <div className="admin-todo-manage__field">
                <label htmlFor="edit-item-title">제목 *</label>
                <input id="edit-item-title" type="text" required value={itemEditForm.title} onChange={(e) => setItemEditForm((f) => ({ ...f, title: e.target.value }))} />
              </div>
              <div className="admin-todo-manage__field">
                <label htmlFor="edit-item-desc">내용</label>
                <textarea id="edit-item-desc" rows={3} value={itemEditForm.description} onChange={(e) => setItemEditForm((f) => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="admin-todo-manage__field">
                <label htmlFor="edit-item-priority">우선순위</label>
                <select id="edit-item-priority" value={itemEditForm.priority} onChange={(e) => setItemEditForm((f) => ({ ...f, priority: e.target.value as TodoPriority }))}>
                  {PRIORITY_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div className="admin-todo-manage__field">
                <label className="admin-todo-manage__checkbox-label">
                  <input type="checkbox" checked={editUseStartDate} onChange={(e) => setEditUseStartDate(e.target.checked)} />
                  시작일 지정
                </label>
                {editUseStartDate && (
                  <input id="edit-item-start" type="date" value={itemEditForm.start_date} onChange={(e) => setItemEditForm((f) => ({ ...f, start_date: e.target.value }))} style={{ marginTop: 8 }} />
                )}
                {!editUseStartDate && (
                  <p className="admin-todo-manage__field-hint">시작일 없이 저장하면 처음부터 표시됩니다.</p>
                )}
              </div>
              <div className="admin-todo-manage__field">
                <label className="admin-todo-manage__checkbox-label">
                  <input type="checkbox" checked={editUseDueDate} onChange={(e) => setEditUseDueDate(e.target.checked)} />
                  마감일 지정
                </label>
                {editUseDueDate && (
                  <input id="edit-item-due" type="date" value={itemEditForm.due_date} onChange={(e) => setItemEditForm((f) => ({ ...f, due_date: e.target.value }))} style={{ marginTop: 8 }} />
                )}
                {!editUseDueDate && (
                  <p className="admin-todo-manage__field-hint">마감일 없이 저장하면 계속 표시됩니다.</p>
                )}
              </div>
              <div className="admin-todo-manage__form-actions">
                <button type="button" className="admin-todo-manage__btn-secondary" onClick={() => { setItemEditOpen(false); setItemEditTarget(null); }}>취소</button>
                <button type="submit" className="admin-todo-manage__btn-primary" disabled={saving}>{saving ? '저장 중…' : '저장'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 공지사항 추가/수정 모달 */}
      {noticeFormOpen && (
        <div className="admin-todo-manage__modal-backdrop" role="presentation" onClick={(e) => { if (e.target === e.currentTarget) setNoticeFormOpen(false); }}>
          <div className="admin-todo-manage__modal admin-todo-manage__modal--wide" role="dialog">
            <div className="admin-todo-manage__modal-header">
              <h2 className="admin-todo-manage__modal-title">{noticeEditTarget ? '공지사항 수정' : '공지사항 새로 쓰기'}</h2>
              <button type="button" className="admin-todo-manage__modal-close" onClick={() => setNoticeFormOpen(false)} aria-label="닫기">×</button>
            </div>
            <form onSubmit={handleNoticeSubmit} className="admin-todo-manage__form">
              <div className="admin-todo-manage__field">
                <label htmlFor="notice-date">날짜</label>
                <input
                  id="notice-date"
                  type="date"
                  value={noticeDateDraft}
                  onChange={(e) => setNoticeDateDraft(e.target.value)}
                />
              </div>
              <div className="admin-todo-manage__field">
                <label htmlFor="notice-content">내용 *</label>
                <textarea
                  id="notice-content"
                  className="admin-todo-manage__notice-form-textarea"
                  rows={12}
                  required
                  value={noticeDraft}
                  onChange={(e) => setNoticeDraft(e.target.value)}
                  placeholder="모두에게 공유할 공지 내용을 입력하세요."
                  autoFocus
                />
              </div>
              <div className="admin-todo-manage__form-actions">
                <button type="button" className="admin-todo-manage__btn-secondary" onClick={() => setNoticeFormOpen(false)}>취소</button>
                <button type="submit" className="admin-todo-manage__btn-primary" disabled={noticeSaving}>{noticeSaving ? '저장 중…' : '저장'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 개인별 메모 작성/수정 모달 */}
      {personalMemoFormOpen && (
        <div className="admin-todo-manage__modal-backdrop" role="presentation" onClick={(e) => { if (e.target === e.currentTarget) setPersonalMemoFormOpen(false); }}>
          <div className="admin-todo-manage__modal admin-todo-manage__modal--wide" role="dialog">
            <div className="admin-todo-manage__modal-header">
              <h2 className="admin-todo-manage__modal-title">{personalMemoEditTarget ? '개인별 메모 수정' : '개인별 메모 새로 쓰기'}</h2>
              <button type="button" className="admin-todo-manage__modal-close" onClick={() => setPersonalMemoFormOpen(false)} aria-label="닫기">×</button>
            </div>
            <form onSubmit={handlePersonalMemoSubmit} className="admin-todo-manage__form">
              <div className="admin-todo-manage__field">
                <label htmlFor="personal-memo-content">내용 *</label>
                <textarea
                  id="personal-memo-content"
                  required
                  className="admin-todo-manage__notice-form-textarea"
                  rows={12}
                  value={personalMemoDraft}
                  onChange={(e) => setPersonalMemoDraft(e.target.value)}
                  placeholder="나만 볼 수 있는 메모를 입력하세요."
                  autoFocus
                />
              </div>
              <div className="admin-todo-manage__form-actions">
                <button type="button" className="admin-todo-manage__btn-secondary" onClick={() => setPersonalMemoFormOpen(false)}>취소</button>
                <button type="submit" className="admin-todo-manage__btn-primary" disabled={personalMemoSaving}>{personalMemoSaving ? '저장 중…' : '저장'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
